import { resendOtpCode, verifyOtpCode } from "api/authentication";
import { redirectLogin } from "api/authentication/helper";
import {
  EXCEEDED_ATTEMPTS_NUMBER_FOR_OTP_CODE,
  EXCEEDED_RESEND_NUMBER,
  INVALID_OTP_CODE,
} from "api/errors";
import { hasErrorCode } from "api/helper";
import { Form, Formik } from "formik";
import useLocalizedRoutes from "hooks/useLocalizedRoutes";
import { Heading, Text, errorToast, successToast } from "iparque-components";
import { AppContext } from "providers/AppProvider";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { convertSecondsToMinutes } from "utils/time";
import FormButton from "../FormButton";
import {
  OTP_CODE_EXPIRATION_TIME,
  OTP_CODE_LENGTH,
  getValidationSchema,
  initialValues,
} from "./configs";
import { FormValues } from "./interfaces";
import OtpInput from "./OtpInput";
import { ResendCodeContainer, TextContainer, UnderlinedText } from "./styles";

const TwoFactorAuthenticationForm: React.FC = () => {
  const { t } = useTranslation();
  const { setIsLoading } = useContext(AppContext);

  const location = useLocation();
  const { authToken } = location.state || {};

  const navigate = useNavigate();
  const [isUrlValid, setIsUrlValid] = useState(false);

  const [seconds, setSeconds] = useState(OTP_CODE_EXPIRATION_TIME);
  const { getLanguageRoute } = useLocalizedRoutes();

  useEffect(() => {
    if (!authToken) {
      errorToast(
        t("7847") /* Erro! */,
        t("7878") /* O url inserido não está correto. */,
        { autoClose: false }
      );

      return;
    }

    setIsUrlValid(true);
  }, [authToken, t]);

  const onSubmitHandler = async (values: FormValues) => {
    setIsLoading(true);

    const { success, data, error } = await verifyOtpCode(
      authToken,
      values.code as number
    );

    if (success && data) {
      const { entities } = data;

      if (entities.length === 1) {
        await redirectLogin(
          { ...entities[0], entityToken: entities[0].token },
          authToken,
          {
            t,
            stopLoading: () => setIsLoading(false),
          }
        );

        return;
      }

      setIsLoading(false);

      navigate(getLanguageRoute("entities"), {
        replace: true,
        state: {
          entities: entities.map((entity) => ({
            ...entity,
            entityToken: entity.token,
          })),
          authToken,
        },
      });

      return;
    }

    setIsLoading(false);

    if (hasErrorCode(INVALID_OTP_CODE, error)) {
      errorToast(
        t("7847") /* Erro! */,
        t("11883") /* Código de validação incorreto. */
      );

      return;
    }

    if (hasErrorCode(EXCEEDED_ATTEMPTS_NUMBER_FOR_OTP_CODE, error)) {
      navigate(getLanguageRoute("login"), { replace: true });

      errorToast(
        t("7847") /* Erro! */,
        t(
          "11882"
        ) /* Foram excedidas o número de tentativas para a introdução do código de validação. */
      );

      return;
    }

    errorToast(
      t("7847") /* Erro! */,
      t("11884") /* Ocorreu um erro ao validar o código. */
    );
  };

  const onClickResendCode = async () => {
    setIsLoading(true);

    const { success, error } = await resendOtpCode(authToken);

    setIsLoading(false);

    if (!success) {
      if (hasErrorCode(EXCEEDED_RESEND_NUMBER, error)) {
        errorToast(
          t("7847") /* Erro! */,
          `${
            t("11886") /* Ocorreu um erro ao reenviar o código de validação. */
          } ${t("11933")}` /* Aguarde um pouco e tente novamente. */
        );

        return;
      }

      errorToast(
        t("7847") /* Erro! */,
        t("11886") /* Ocorreu um erro ao reenviar o código de validação */
      );

      return;
    }

    setSeconds(OTP_CODE_EXPIRATION_TIME);

    successToast(
      t("586" /* Sucesso */),
      t("11885") /* Foi enviado um novo código de validação para o seu email */
    );
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      setSeconds((prevSeconds) => {
        if (prevSeconds > 0) {
          return prevSeconds - 1;
        }

        clearInterval(intervalId);
        return 0;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  const minutes = convertSecondsToMinutes(seconds);
  const remainingSeconds = seconds % 60;

  return (
    <>
      <Heading className="mt-10" variant="h8">
        {
          t("11877", {
            length: OTP_CODE_LENGTH,
          }) /* Para uma maior segurança da sua conta, foi enviado um código de validação de {{length}} dígitos para o seu email. */
        }
      </Heading>
      <br />
      <Formik
        initialValues={initialValues}
        validationSchema={getValidationSchema(t)}
        onSubmit={onSubmitHandler}
      >
        {({ isValid, dirty, setFieldValue }) => (
          <Form>
            <OtpInput
              className="mt-15 mb-15"
              disabled={!isUrlValid}
              length={OTP_CODE_LENGTH}
              onChange={(value: string) => setFieldValue("code", value)}
            />
            {isUrlValid && (
              <Text color="quaternary" variant="body8" className="mt-15 mb-10">
                {t("11890") /* O código enviado expira em */}&nbsp;
                {String(minutes).padStart(2, "0")}:
                {String(remainingSeconds).padStart(2, "0")}
              </Text>
            )}
            <FormButton
              type="submit"
              disabled={!isUrlValid || !isValid || !dirty}
            >
              {t("2783") /* Validar */}
            </FormButton>
          </Form>
        )}
      </Formik>
      {isUrlValid && (
        <ResendCodeContainer>
          <Text color="quaternary" variant="body6">
            {t("11878") /* Pode demorar um minuto até receber o seu código. */}
          </Text>
          <Text color="quaternary" variant="body6">
            {t("11879") /* Não recebeu? */}
            &nbsp;
            <TextContainer onClick={onClickResendCode}>
              <UnderlinedText variant="body6">
                {t("11880") /* Reenviar um novo código. */}
              </UnderlinedText>
            </TextContainer>
          </Text>
        </ResendCodeContainer>
      )}
    </>
  );
};

export default TwoFactorAuthenticationForm;
